import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  FileText,
  Briefcase,
  Clock,
  MapPin,
  ArrowRight,
  CheckCheck,
  History,
  Inbox,
  PartyPopper,
  Megaphone,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const TIPO_ICON = {
  fichaje_recordatorio: Clock,
  entrada_temprana_patron: Clock,
  ausencia_justificacion: CalendarDays,
  compensacion: Briefcase,
  horario_cambio: Clock,
  festivo_cambio: PartyPopper,
  comunicado: Megaphone,
  ficha_empleado: Users,
  zona_trabajo: MapPin,
  solicitud_confirmacion: Inbox,
  solicitud_revisada: CalendarDays,
  intercambio_pareja: CalendarDays,
  intercambio: CalendarDays,
  documento: FileText,
  proyecto: Briefcase,
};

const TIPO_ACCENT = {
  fichaje_recordatorio: "border-amber-200 bg-amber-50/80",
  entrada_temprana_patron: "border-orange-200 bg-orange-50/70",
  ausencia_justificacion: "border-rose-200 bg-rose-50/60",
  compensacion: "border-violet-200 bg-violet-50/60",
  horario_cambio: "border-sky-200 bg-sky-50/60",
  festivo_cambio: "border-orange-200 bg-orange-50/60",
  comunicado: "border-indigo-200 bg-indigo-50/60",
  ficha_empleado: "border-emerald-200 bg-emerald-50/60",
  zona_trabajo: "border-fuchsia-200 bg-fuchsia-50/60",
  solicitud_confirmacion: "border-indigo-300 bg-indigo-50/80 ring-1 ring-indigo-100",
  solicitud_revisada: "border-emerald-200 bg-emerald-50/50",
  intercambio_pareja: "border-orange-200 bg-orange-50/60",
  intercambio: "border-orange-200 bg-orange-50/60",
  documento: "border-slate-200 bg-slate-50/60",
  proyecto: "border-violet-200 bg-violet-50/60",
};

function AvisoCard({ item, onAbrir }) {
  const Icon = TIPO_ICON[item.tipo] || Bell;
  const accent = TIPO_ACCENT[item.tipo] || "border-zinc-200 bg-white";
  const pendiente = item.pendiente_accion;

  return (
    <article
      className={`rounded-xl border p-4 shadow-sm transition-shadow hover:shadow-md ${accent} ${pendiente ? "" : "opacity-90"}`}
      data-testid={`aviso-${item.id}`}
    >
      <div className="flex gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${pendiente ? "bg-white shadow-sm" : "bg-zinc-100"}`}>
          <Icon className="h-5 w-5 text-indigo-600" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  {item.categoria}
                </Badge>
                {item.ambito_label && (
                  <Badge
                    variant="outline"
                    className={
                      item.ambito === "gerencia"
                        ? "border-teal-200 bg-teal-50 text-[10px] text-teal-800"
                        : "border-sky-200 bg-sky-50 text-[10px] text-sky-800"
                    }
                  >
                    {item.ambito_label}
                  </Badge>
                )}
                {pendiente && (
                  <Badge className="bg-amber-500 text-[10px] hover:bg-amber-500">Pendiente</Badge>
                )}
                {item.corregida && !pendiente && (
                  <Badge className="bg-emerald-600 text-[10px] hover:bg-emerald-600">Corregida</Badge>
                )}
                {item.leida && !pendiente && !item.corregida && (
                  <Badge variant="secondary" className="text-[10px]">Leído</Badge>
                )}
              </div>
              <h3 className="mt-1 font-semibold text-zinc-900">{item.titulo}</h3>
            </div>
            <time className="shrink-0 text-xs text-zinc-500 whitespace-nowrap">
              {fmtFecha(item.created_at)} · {fmtHora(item.created_at)}
            </time>
          </div>
          {item.mensaje && (
            <p className="mt-2 text-sm leading-relaxed text-zinc-600 line-clamp-3">{item.mensaje}</p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-3 h-8 gap-1 px-0 text-indigo-600 hover:text-indigo-800"
            onClick={() => onAbrir(item)}
          >
            {pendiente ? "Ir a resolver" : item.corregida ? "Ya corregida" : "Ver detalle"}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </article>
  );
}

export default function MisNotificaciones() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pendientes");
  const [data, setData] = useState({ items: [], total_pendientes: 0 });
  const [cargando, setCargando] = useState(true);
  const [marcando, setMarcando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data: res } = await api.get("/notificaciones", {
        params: { historial: tab === "historial" },
      });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [tab]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirAviso = async (item) => {
    const marcarAlAbrir =
      item.id.startsWith("fichaje:")
      || item.id.startsWith("zona:")
      || (item.id.startsWith("solicitud:") && !item.id.startsWith("solicitud_conf:"));

    if (marcarAlAbrir) {
      try {
        await api.put(`/notificaciones/${encodeURIComponent(item.id)}/leida`);
        window.dispatchEvent(new Event("notificaciones-actualizar"));
        await cargar();
      } catch {
        /* seguir navegando aunque falle el marcado */
      }
    }
    navigate(item.url);
  };

  const marcarTodas = async () => {
    setMarcando(true);
    try {
      const { data: res } = await api.put("/notificaciones/leidas");
      toast.success(
        res.marcadas > 0
          ? `${res.marcadas} aviso(s) marcado(s) como leído(s)`
          : "No hay avisos informativos pendientes de leer"
      );
      window.dispatchEvent(new Event("notificaciones-actualizar"));
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarcando(false);
    }
  };

  const pendientes = tab === "pendientes" ? data.items : data.items.filter((i) => i.pendiente_accion);
  const historialItems = tab === "historial" ? data.items : [];

  return (
    <div className="space-y-5" data-testid="mis-notificaciones-page">
      <PageHeader
        icon={Bell}
        title="Mis avisos"
        subtitle="Recordatorios, solicitudes, cambios de horario y zonas"
        gradient="from-amber-500 via-orange-500 to-rose-500"
      >
        {data.total_pendientes > 0 && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="gap-1.5 bg-white/20 text-white hover:bg-white/30 border-0"
            onClick={marcarTodas}
            disabled={marcando}
            data-testid="marcar-avisos-leidas"
          >
            <CheckCheck className="h-4 w-4" />
            Marcar leídas
          </Button>
        )}
      </PageHeader>

      <div className="flex gap-2 rounded-xl border border-zinc-200 bg-white p-1 shadow-sm">
        <button
          type="button"
          onClick={() => setTab("pendientes")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-medium transition-colors ${
            tab === "pendientes" ? "bg-amber-100 text-amber-900" : "text-zinc-500 hover:text-zinc-700"
          }`}
          data-testid="tab-avisos-pendientes"
        >
          <Bell className="h-4 w-4" />
          Pendientes
          {data.total_pendientes > 0 && (
            <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {data.total_pendientes > 99 ? "99+" : data.total_pendientes}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab("historial")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-medium transition-colors ${
            tab === "historial" ? "bg-indigo-100 text-indigo-900" : "text-zinc-500 hover:text-zinc-700"
          }`}
          data-testid="tab-avisos-historial"
        >
          <History className="h-4 w-4" />
          Historial
        </button>
      </div>

      {tab === "pendientes" && (
        <p className="text-xs text-zinc-500">
          Al pulsar «Ir a resolver» en avisos de fichaje o zonas, el aviso se marca como leído. Las solicitudes que
          requieren confirmación permanecen hasta resolverlas en{" "}
          <Link to="/mis-solicitudes" className="text-indigo-600 hover:underline">Solicitudes</Link>.
        </p>
      )}

      {cargando ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-950" />
        </div>
      ) : (tab === "pendientes" ? pendientes : historialItems).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/80 px-6 py-12 text-center">
          <Bell className="mx-auto h-10 w-10 text-zinc-300" strokeWidth={1.25} />
          <p className="mt-3 font-medium text-zinc-700">
            {tab === "pendientes" ? "No tienes avisos pendientes" : "Sin avisos en el historial reciente"}
          </p>
          <p className="mt-1 text-sm text-zinc-500">
            {tab === "pendientes"
              ? "Cuando recibas recordatorios o revisiones aparecerán aquí."
              : "El historial muestra los últimos 6 meses de avisos ya consultados."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {(tab === "pendientes" ? pendientes : historialItems).map((item) => (
            <AvisoCard key={item.id} item={item} onAbrir={abrirAviso} />
          ))}
        </div>
      )}
    </div>
  );
}
